import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDPrimaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import MemberDataCaptureCurrentActionItemLayout
  from 'Components/ComponentLibrary/Organisms/MemberDataCapture/Layouts/MemberDataCaptureCurrentActionItemLayout';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { noop } from 'Helpers/PropTypes';
import { DataValueMap } from 'Helpers/Testing';
import { useSnackbarContext } from 'Context/SnackbarContext';
import {
  AttachmentsSectionRebranded,
} from 'Components/ComponentLibrary/Components/Uncategorized/AttachmentsSectionRebranded';
import FileUploadDropArea from 'Components/ComponentLibrary/Molecules/FileUploadDropArea';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';

export default function MemberDataCaptureTaxReturnDocumentLayout({ isReadOnly }) {
  const { logEvent, logEventSuccess, logEventFailure } = useEventTrackingLogEvent({ nodeName: 'MDC Upload Tax Return Document Step' });
  const { displayErrorMessage } = useSnackbarContext();
  const { resource, onSubmit } = useMetaLayoutContext();
  const [isUploading, setIsUploading] = React.useState(false);
  const [currentResource, setCurrentResource] = React.useState(resource);
  const [loading, setLoading] = useState(false);

  const logRelationshipEvent = (eventName, logEventFn = logEvent) => currentResource.withRelationship(
    fr.relationships.uploadFile.name,
    () => logEventFn(eventName, {}),
    noop,
  );

  useComponentDidMount(() => {
    logRelationshipEvent('Add tax return document wizard step loaded');
  });

  const uploadRel = currentResource.relationship(fr.relationships.uploadFile.name);
  const hasUploadRelationship = !!uploadRel;

  const refreshResource = useCallback(async () => {
    const refreshedCollection = await resource.link('self').get();
    setCurrentResource(refreshedCollection.resources().head());
  }, [resource]);


  const handleUploadComplete = async (success) => {
    await refreshResource();

    if (success) {
      logRelationshipEvent('Add tax return document wizard step upload success', logEventSuccess);
      return;
    }
    logRelationshipEvent('Add tax return document wizard step upload error', logEventFailure);
  };

  const handleOnClickSubmit = async () => {
    try {
      setLoading(true);
      await onSubmit();
    } catch (err) {
      displayErrorMessage('Failed to submit current member data capture item');
    }
    setLoading(false);
  };

  const handleDeleteAttachment = async (attachment) => {
    await attachment.delete();
    await refreshResource();
  };

  const attachmentsCollection = currentResource?.attr('taxReturnDocuments');

  return (
    <FDStack direction={'column'} alignItems={'stretch'} spacing={2}>
      <MetaControlField attr={'title'} />
      <MetaControlField attr={'subtitle'} />
      {hasUploadRelationship
        ? <FileUploadDropArea
          trailingTitleText={'most recent tax return(s)'}
          subtitle={'JPG, PNG, PDF with max file size 50MB'}
          url={uploadRel.link('related').url()}
          acceptedFileTypes="image/*,.pdf"
          onUploadStart={() => setIsUploading(true)}
          onUploadEnd={() => setIsUploading(false)}
          multiple
          onSuccess={() => handleUploadComplete(true)}
          onError={() => handleUploadComplete(false)}
        /> : null}
      {
         hasUploadRelationship
           ? (<AttachmentsSectionRebranded
             attachmentsCollection={attachmentsCollection}
             AttachmentProps={{
               onDelete: handleDeleteAttachment,
             }}
             eventCategory={'Member Data Capture'}
           />) : null
      }
      <MetaLayoutRemainingFields noAccordion />
      <FDPrimaryActionButton
        {...DataValueMap.init('member-data-capture-current-submit').build()}
        label="Submit"
        size={'lg'}
        isFullWidth
        disabled={isReadOnly || isUploading || attachmentsCollection?.resources()?.length() === 0} // Don't allow submissions if there are no attachments, or mid-upload
        loading={loading}
        onClick={handleOnClickSubmit}
      />
    </FDStack>
  );
}

MemberDataCaptureCurrentActionItemLayout.propTypes = {
  isReadOnly: PropTypes.bool.isRequired,
};
